/* eslint-disable max-lines */
import { Button, Textarea, Toggle } from '@hakimo-ui/shared/ui-base';
import {
  ClockIcon,
  TrashIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { SOPNote, TimeRange } from '@hakimo-ui/hakimo/types';
import { useState } from 'react';
import CameraSelectionModal from './camera-selection-modal/CameraSelectionModal';
import CameraLabels from './camera-labels/CameraLabels';
import TimeRangeSelectionModal from './time-range-selection-modal/TimeRangeSelectionModal';
import TimeRangeLabels from './time-range-labels/TimeRangeLabels';

interface Props {
  notes: SOPNote[];
  onChange: (val: SOPNote[]) => void;
  locationId?: string;
}

export function WorkflowFormNotes(props: Props) {
  const { notes, onChange, locationId } = props;
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<number | null>(
    null
  );
  const [timeRangeModalOpen, setTimeRangeModalOpen] = useState(false);
  const [selectedNoteIndexForTimeRange, setSelectedNoteIndexForTimeRange] =
    useState<number | null>(null);

  const onInputChange = (index: number) => (val: string) => {
    const updatedNotes = [...notes];
    updatedNotes[index] = { ...updatedNotes[index], text: val };
    onChange(updatedNotes);
  };

  const onToggleChange = (index: number) => (enabled: boolean) => {
    const updatedNotes = [...notes];
    updatedNotes[index] = {
      ...updatedNotes[index],
      isRelevantForAiOperator: enabled,
    };
    onChange(updatedNotes);
  };

  const onDeleteNote = (index: number) => () => {
    const updatedNotes = [...notes];
    updatedNotes.splice(index, 1);
    onChange(updatedNotes);
  };

  const onCameraIconClick = (index: number) => () => {
    if (!locationId) return;
    setSelectedNoteIndex(index);
    setCameraModalOpen(true);
  };

  const onCameraSelectionSubmit = (selectedCameraNames: string[]) => {
    if (selectedNoteIndex !== null) {
      const updatedNotes = [...notes];
      updatedNotes[selectedNoteIndex] = {
        ...updatedNotes[selectedNoteIndex],
        cameras: selectedCameraNames,
      };
      onChange(updatedNotes);
    }
  };

  const onCameraModalClose = () => {
    setCameraModalOpen(false);
    setSelectedNoteIndex(null);
  };

  const onTimeRangeIconClick = (index: number) => () => {
    setSelectedNoteIndexForTimeRange(index);
    setTimeRangeModalOpen(true);
  };

  const onTimeRangeSelectionSubmit = (timeRange: TimeRange | null) => {
    if (selectedNoteIndexForTimeRange !== null) {
      const updatedNotes = [...notes];
      updatedNotes[selectedNoteIndexForTimeRange] = {
        ...updatedNotes[selectedNoteIndexForTimeRange],
        timeRange: timeRange || undefined,
      };
      onChange(updatedNotes);
    }
  };

  const onTimeRangeModalClose = () => {
    setTimeRangeModalOpen(false);
    setSelectedNoteIndexForTimeRange(null);
  };

  return (
    <div className="space-y-4">
      {notes.map((note, i) => (
        <div key={i} className="space-y-2">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Textarea
                value={note.text}
                onChange={onInputChange(i)}
                placeholder="Enter note text..."
                rows={3}
              />
            </div>
            <div className="mt-2 flex flex-col items-center justify-center gap-1">
              <div className="flex-shrink-0">
                <Toggle
                  enabled={note.isRelevantForAiOperator}
                  onChange={onToggleChange(i)}
                  label="AI Operator"
                  type="positive"
                />
              </div>
              <div className="flex items-center justify-center">
                {locationId && (
                  <div>
                    <Button
                      variant="icon"
                      onClick={onCameraIconClick(i)}
                      title="Associate Camera with this note"
                    >
                      <VideoCameraIcon className="h-5 w-5" />
                    </Button>
                  </div>
                )}
                <div>
                  <Button
                    variant="icon"
                    onClick={onTimeRangeIconClick(i)}
                    title="Add Time Range"
                  >
                    <ClockIcon className="h-5 w-5" />
                  </Button>
                </div>
                <div>
                  <Button variant="icon" onClick={onDeleteNote(i)}>
                    <TrashIcon className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <TimeRangeLabels timeRange={note.timeRange} />
          <CameraLabels cameras={note.cameras} />
        </div>
      ))}
      {cameraModalOpen && locationId && selectedNoteIndex !== null && (
        <CameraSelectionModal
          open={cameraModalOpen}
          onClose={onCameraModalClose}
          onSubmit={onCameraSelectionSubmit}
          locationId={locationId}
          initialSelectedCameras={notes[selectedNoteIndex]?.cameras || []}
        />
      )}
      {timeRangeModalOpen && selectedNoteIndexForTimeRange !== null && (
        <TimeRangeSelectionModal
          open={timeRangeModalOpen}
          onClose={onTimeRangeModalClose}
          onSubmit={onTimeRangeSelectionSubmit}
          initialTimeRange={notes[selectedNoteIndexForTimeRange]?.timeRange}
        />
      )}
    </div>
  );
}

export default WorkflowFormNotes;
